import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await register(name, email, password);
        toast.success("Welcome to DAIONE");
        navigate("/onboarding");
      } else {
        await login(email, password);
        toast.success("Welcome back");
        navigate("/app/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <div className="dark-island hidden lg:flex flex-1 relative overflow-hidden border-r border-white/10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(https://images.pexels.com/photos/13106627/pexels-photo-13106627.jpeg)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(20%) brightness(0.5)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        <div className="relative p-16 flex flex-col justify-between">
          <Link to="/" className="flex items-center" data-testid="auth-brand-link">
            <BrandMark variant="wordmark" size={120} />
          </Link>
          <div>
            <h2 className="font-display text-5xl leading-none mb-4 max-w-md">
              The only coach that adapts <span className="text-[#6B9BFF]">to you.</span>
            </h2>
            <p className="text-white/60 max-w-md">
              Personalized workouts, AI-powered nutrition tracking, real progress.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-up">
          <div className="label-eyebrow text-[#6B9BFF] mb-3">
            {mode === "signup" ? "Create account" : "Welcome back"}
          </div>
          <h1 className="font-display text-4xl mb-8 leading-none">
            {mode === "signup" ? "Forge your path." : "Sign in to train."}
          </h1>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <label className="label-eyebrow text-white/50 block mb-2">Name</label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#6B9BFF]"
                  placeholder="Your name"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="label-eyebrow text-white/50 block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#6B9BFF]"
                placeholder="you@example.com"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="label-eyebrow text-white/50 block mb-2">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#121212] border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-[#6B9BFF]"
                placeholder="Min 6 characters"
                data-testid="auth-password-input"
              />
            </div>
            <button
              disabled={busy}
              type="submit"
              className="w-full bg-[#6B9BFF] text-black font-black uppercase tracking-widest py-4 hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="auth-submit-button"
            >
              {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {mode !== "signup" && (
            <div className="mt-4 text-center">
              <Link
                to="/auth/recover"
                className="text-xs uppercase tracking-widest text-white/55 hover:text-[#6B9BFF]"
                data-testid="auth-forgot-password"
              >
                Forgot password?
              </Link>
            </div>
          )}

          <div className="mt-8 text-sm text-white/60 text-center">
            {mode === "signup" ? "Already have an account?" : "New here?"}{" "}
            <button
              onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              className="text-[#6B9BFF] hover:underline font-bold"
              data-testid="auth-toggle-mode"
            >
              {mode === "signup" ? "Sign in" : "Create one"}
            </button>
          </div>

          <Link to="/" className="block mt-6 text-center text-xs text-white/40 hover:text-white/70" data-testid="auth-back-home">
            ← Back to home
          </Link>
          <Link to="/privacy" className="block mt-2 text-center text-xs text-white/30 hover:text-white/60" data-testid="auth-privacy-link">
            Privacy policy
          </Link>
        </div>
      </div>
    </div>
  );
}
